import React from 'react';

import Select from 'react-select';
import CustomIcon from '../Common/CustomIcon/CustomIcon';

import style from './Listings.module.css';

import {
	createBuildings,
	deleteBuildings,
	updateBuilding,
} from 'Vulture/Buildings';

class BuildingsModal extends React.Component {
	constructor(props) {
		super(props);

		let selectedSystemGroup = {
			label: 'Select System Group',
			value: '',
		};

		this.systemGroups = [
			...Object.entries(props.tableSettings)
				.filter(([ m1Key, ]) => {
					if (!props.systemGroupUUID) {
						return true;
					}
					return m1Key === `system_group.${props.systemGroupUUID}`;
				}).map(([ m1Key, display_name, ]) => {
					const m1UUID = m1Key.split('.')[1];

					const option = {
						label: display_name,
						value: m1UUID,
					};

					if (m1UUID === props.originalSystemGroupUUID) {
						selectedSystemGroup = option;
					}

					return option;
				}),
		];

		// Default to the sole group only when creating. On an edit the loop above has
		// already selected the building's own group; defaulting here would mean a
		// rename of an UNASSIGNED building silently assigns it that group.
		if (this.systemGroups.length === 1 && !props.isEdit) {
			selectedSystemGroup = this.systemGroups[0];
		}

		this.state = {
			buildingName: props.buildingName || '',
			displayName: props.displayName || '',
			selectedSystemGroup,
			groupChanged: false,
			isSaving: false,
		};
	}

	componentDidMount() {
	}

	saveChanges() {
		const {
			app,
			accountUUID,
			buildingM1,
			isEdit,
			closeModal,
		} = this.props;

		const {
			buildingName,
			displayName,
			selectedSystemGroup,
			groupChanged,
		} = this.state;

		// The Save button is only styled disabled ('button-disabled' does not block
		// clicks), so enforce the rule here: a new building needs a system group.
		// Editing is allowed without one — an unassigned building must still be
		// renameable.
		if (!isEdit && (!selectedSystemGroup || !selectedSystemGroup.value)) {
			return;
		}

		// The modal stays on screen for the whole round trip and there is no spinner, so
		// a second click is easy. A create is not idempotent — building_name has no unique
		// key — so two clicks would leave two buildings. Checked after the validation
		// return above, which sends no request.
		if (this.state.isSaving) {
			return;
		}

		this.setState({
			isSaving: true,
		});

		// On failure keep the modal open so the user's input survives the error. The flag
		// must be cleared here too, or the modal can never be saved again.
		const finish = (res) => {
			if (res && res.error) {
				this.setState({
					isSaving: false,
				});

				if (typeof app.setNetworkErrorModal === 'function') {
					app.setNetworkErrorModal(false, res.message);
				}

				return;
			}

			app.fetchAccountBuildings();
			closeModal();
		};

		if (!isEdit) {
			// The table did not always pass accountUUID; without it the create is
			// refused for scoped callers (and would be unowned for global ones).
			const acct = accountUUID || (app && app.state && app.state.accountUUID) || '';

			createBuildings(acct, buildingName, selectedSystemGroup.value, displayName)
				.then(finish);
		} else {
			const buildingUUID = buildingM1.split('.')[1];

			// The group is sent only when the user changed it. The options can omit the
			// building's own group (hidden for narrow-scoped users), and resending the
			// untouched selection would then unassign it on a plain rename.
			const group = groupChanged ? selectedSystemGroup.value : undefined;

			updateBuilding(buildingUUID, buildingName, group, displayName)
				.then(finish);
		}
	}

	render() {
		const {
			closeModal,
			systemGroupUUID,
			isEdit,
			app,
			accountUUID,
			buildingM1,
		} = this.props;

		const {
			buildingName,
			displayName,
			selectedSystemGroup,
			isSaving,
		} = this.state;

		let defaultValue = {
			label: 'Select System Group',
			value: '',
		};

		if (systemGroupUUID !== '') {
			defaultValue = this.systemGroups[0];
		}

		let saveButtonClassName = 'button';

		if (isSaving || (!isEdit && selectedSystemGroup.value === '')) {
			saveButtonClassName += ' button-disabled';
		}

		let secondButton = (
			<button
				onClick={(e) => {
					e.preventDefault();
					closeModal();
				}}
				className='button button-outlined'
				data-test-id='buildingGoBackButton'
			>
				<CustomIcon icon='undo' /> Go Back
			</button>
		);

		if (isEdit) {
			secondButton = (
				<button
					onClick={(e) => {
						e.preventDefault();

						const {
							buildingsView,
						} = this.props;

						// vals carries the name the error message quotes back.
						const param = [
							{
								m1Key: buildingM1,
								vals:  {
									buildingName: this.props.buildingName,
								},
							},
						];

						// The table passes accountUUID on Add New but not on Edit.
						const acct = accountUUID || (app && app.state && app.state.accountUUID) || '';

						buildingsView.setState({
							confirmModal:        true,
							confirmModalContent: (
								<>
									<p>{`Delete this building?`}</p>
									<div className="confirm-modal-buttons">
										<button className="button" data-test-id="buildingDeleteConfirmYesButton" onClick={(e) => {
											e.preventDefault();
											deleteBuildings(param, acct)
												.then((res) => {
													closeModal();

													if (res && res.error) {
														// Re-fetch after a failure too: the row may
														// be gone, or already deleted elsewhere.
														if (typeof app.setNetworkErrorModal === 'function') {
															app.setNetworkErrorModal(false, res.message);
														}
														app.fetchAccountBuildings();

														return;
													}

													app.fetchAccountBuildings();
												});
											buildingsView.setState({
												confirmModal:        false,
												confirmModalContent: '',
											});
										}}>Yes</button>
										<button className="button" data-test-id="buildingDeleteConfirmNoButton" onClick={(e) => {
											e.preventDefault();
											buildingsView.setState({
												confirmModal:        false,
												confirmModalContent: '',
											});
										}}>No</button>
									</div>
								</>
							),
						});
					}}
					className='button button-outlined'
					data-test-id='buildingDeleteButton'
				>
					<CustomIcon icon='trash' /> Delete
				</button>
			);
		}

		return (
			<>
				<div className={style.listingsModal}>
					<div className={style.listing}>{this.state.name}</div>
					<div className={style.listing}>{this.state.suite}</div>
					<div className={`${style.listingsModalContainer} ${this.state.isIntegration && style.integrationModalContainer}`}>
						<div className={style.column1}>
							<div className={style.sectionLabel}>Details</div>
							<div className={`text-small`}>
								<div className="label">
									<label>Building Name</label>
									<span style={{ fontSize: '0.8em', color: '#888', marginLeft: '4px' }}>
										(not editable. wayfinding dependant)
									</span>
								</div>
								<input
									type='text'
									data-test-id='buildingNameInput'
									value={buildingName}
									disabled={isEdit}
									onChange={(e) => {
										if (buildingName === e.target.value) {
											return;
										}

										this.setState({
											buildingName: e.target.value,
										});
									}}
								/>
							</div>
							<div className={`text-small`}>
								<div className="label">
									<label>Display Name</label>
								</div>
								<input
									type='text'
									data-test-id='buildingDisplayNameInput'
									value={displayName}
									onChange={(e) => {
										if (displayName === e.target.value) {
											return;
										}

										this.setState({
											displayName: e.target.value,
										});
									}}
								/>
							</div>
							<div className={`drop-down`}>
								<div className="label">
									<label>System Group</label>
								</div>
								<div data-test-id='buildingSystemGroupSelect'>
									<Select
										defaultValue={defaultValue}
										options={this.systemGroups}
										value={selectedSystemGroup}
										onChange={(selectedOption) => {
											this.setState({
												selectedSystemGroup: selectedOption,
												groupChanged:        true,
											});
										}}
									/>
								</div>
							</div>
						</div>
					</div>
					<div className={style.bottom}>
						{secondButton}
						<button
							className={saveButtonClassName}
							disabled={isSaving}
							data-test-id='buildingSaveButton'
							onClick={(e) => {
								e.preventDefault();

								this.saveChanges();
							}}
						>
							<CustomIcon icon='save' />Save
						</button>
					</div>
				</div>
			</>
		);
	}
}

export default BuildingsModal;